import { AdvFilterResult, AutoCompleteKeyWord } from "components/AdvFilter";
import { CmdActiveOnly, CmdDeepTeam, CmdInstitutional, CmdOnlyInstitutional, CmdPost, CmdQuestion, CmdTopic, CmdTopicNoSubs, CmdWithVideo } from "components/AdvFilter/CommonCommands";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiPaper from "components/MuiExtras/MuiPaper";
import { WithSnackbar, withSnackbar } from "components/SnackbarProvider/Snackbar";
import { MemberType, PublicationType } from "models/ApiEnums";
import { DashboardInstitutionalFilterOption, DashboardUserAndGroupsFilter, DashboardUserAndGroupsParticipationSummaryColumn, MemberSuggestionDto } from "models/ApiModels";
import * as React from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import Session from "services/Session";
import { DashboardUserAndGroupsAnswerCommands, DashboardUserAndGroupsKnowledgeCommands, DashboardUserAndGroupsKnowledgeTestCommands, DashboardUserAndGroupsTeamCommands, DashboardUserAndGroupsTeamKnowledgeTestCommands, IUserAndGroupsFilterProps } from "views/Dashboards/UserAndGroupsDashboard/Common";
import { ParticipationAccessGraph } from "views/Dashboards/UserAndGroupsDashboard/ParticipationAccess";
import { ParticipationKnowledgeTestGraph } from "views/Dashboards/UserAndGroupsDashboard/ParticipationKnowledgeTest";
import { ParticipationPublicationGraph } from "views/Dashboards/UserAndGroupsDashboard/ParticipationPublication";
import ParticipationTiles from "views/Dashboards/UserAndGroupsDashboard/ParticipationTiles";
import { TabOptions, UserAndGroupsTabs } from "views/Dashboards/UserAndGroupsDashboard/UserAndGroupsTabs";

/////////////////////////////////////////////////////////////////////////////////////

interface IState {
    participationTile: DashboardUserAndGroupsParticipationSummaryColumn;
    advFilters: AdvFilterResult[];
}

interface IProps {
    onTabChange: (tabOptions: TabOptions) => void;
    filterComponent: React.ComponentType<IUserAndGroupsFilterProps>;
    managerTeamMode: boolean;
}

type Props = IProps & WithSnackbar & WithTranslation;

/////////////////////////////////////////////////////////////////////////////////////

class UserAndGroupsParticipationDashboard extends React.PureComponent<Props, IState> {

    constructor(props: Props) {
        super(props);

        this.state = {
            participationTile: DashboardUserAndGroupsParticipationSummaryColumn.Accesses,
            advFilters: []
        };
    }

    ///
    private handleSelectTopic = (topicId: string, name: string) => {
        this.setState(({ advFilters }) => {

            advFilters = advFilters.filter(p => p.keyword != CmdTopic);

            if (this.state.participationTile == DashboardUserAndGroupsParticipationSummaryColumn.KnowledgeTests) {
                // Garante User XOR Topic
                advFilters = advFilters.filter(p => p.keyword != AutoCompleteKeyWord);
            }

            advFilters.push({
                keyword: CmdTopic,
                value: topicId,
                text: name
            });

            return { advFilters };
        }, () => this.props.snackbar.showMessage(this.props.t("Views.Dashboards.Common.FilteringPubTopic", { name: name })));
    };

    ///
    private handleSelectUser = (userId: string, name: string, thumbnailUrl: string) => {

        this.setState(({ advFilters }) => {

            advFilters = advFilters.filter(p => p.keyword != AutoCompleteKeyWord);

            if (this.state.participationTile == DashboardUserAndGroupsParticipationSummaryColumn.KnowledgeTests) {
                // Garante User XOR Topic
                advFilters = advFilters.filter(p => p.keyword != CmdTopic && p.keyword != CmdTopicNoSubs);
            }

            advFilters.push({
                keyword: AutoCompleteKeyWord,
                value: { key: userId, label: name, thumbnailUrl: thumbnailUrl, memberType: MemberType.User } as MemberSuggestionDto,
                text: name
            });

            return { advFilters };
        }, () =>
            this.props.snackbar.showMessage(
                this.props.t(
                    this.state.participationTile == DashboardUserAndGroupsParticipationSummaryColumn.Publications
                        ? "Views.Dashboards.Common.FilteringPubAuthor"
                        : "Views.Dashboards.Common.FilteringAnswers"
                    , { name: name })
            )
        );
    };


    ///
    public render() {
        const { advFilters, participationTile } = this.state;
        const { filterComponent: Filter, managerTeamMode } = this.props;

        var userFilter = advFilters.find(p => p.keyword == AutoCompleteKeyWord);

        const withInstitutional = advFilters.findIndex(p => p.keyword == CmdInstitutional) > -1;
        const onlyInstitutional = advFilters.findIndex(p => p.keyword == CmdOnlyInstitutional) > -1;
        const withVideo = advFilters.find(p => p.keyword == CmdWithVideo);
        const activeOnly = advFilters.find(p => p.keyword == CmdActiveOnly);

        const topic = advFilters.find(p => p.keyword == CmdTopic);
        const includeSubtopics = advFilters.find(p => p.keyword == CmdTopicNoSubs) == null;

        const publicationTypes: PublicationType[] = [];
        if (advFilters.find(p => p.keyword == CmdPost)) publicationTypes.push(PublicationType.Post);
        if (advFilters.find(p => p.keyword == CmdQuestion)) publicationTypes.push(PublicationType.Question);

        const member = userFilter == null ? null : userFilter.value as MemberSuggestionDto;

        const deepTeam = advFilters.find(p => p.keyword == CmdDeepTeam);

        const filter: DashboardUserAndGroupsFilter = {

            memberId: member == null ? null : member.key,
            memberType: member == null ? null : member.memberType,

            managerId: this.props.managerTeamMode ? Session.user.id : null,
            managerDeepTeam: this.props.managerTeamMode && deepTeam != null ? true : null,

            topicId: topic?.value,
            includeSubtopics: includeSubtopics,

            institutionalOption: withInstitutional
                ? DashboardInstitutionalFilterOption.Both
                : onlyInstitutional
                    ? DashboardInstitutionalFilterOption.OnlyInstitutional
                    : DashboardInstitutionalFilterOption.OnlyNonInstitutional,
            publicationTypes: publicationTypes.length == 0 ? null : publicationTypes,
            withVideo: withVideo == null ? null : true,
            activeOnly: activeOnly == null ? null : true
        };

        return (
            <>
                <Filter
                    advFilters={advFilters}
                    commandTypeDef={
                        participationTile == DashboardUserAndGroupsParticipationSummaryColumn.Accesses
                            ? null
                            : participationTile == DashboardUserAndGroupsParticipationSummaryColumn.Answers
                                ? DashboardUserAndGroupsAnswerCommands()
                                : participationTile == DashboardUserAndGroupsParticipationSummaryColumn.KnowledgeTests
                                    ? (managerTeamMode ? DashboardUserAndGroupsTeamKnowledgeTestCommands() : DashboardUserAndGroupsKnowledgeTestCommands())
                                    : (managerTeamMode ? DashboardUserAndGroupsTeamCommands() : DashboardUserAndGroupsKnowledgeCommands())
                    }
                    onFilterChange={(advFilters) => this.setState({ advFilters })}
                />

                <UserAndGroupsTabs currentTab={TabOptions.Participation} onTabChange={this.props.onTabChange} />

                <ParticipationTiles
                    {...this.props}
                    activeTile={participationTile}
                    filter={filter}
                    onChange={
                        (tile) => {
                            this.setState({ participationTile: tile });
                        }
                    }
                />

                <MuiContainer margin="normal">
                    <MuiPaper borderVariant="accentedRound" borderColor="warning">
                        {
                            participationTile == DashboardUserAndGroupsParticipationSummaryColumn.Accesses
                                ? <ParticipationAccessGraph {...this.props} tile={participationTile} filter={filter} onSelectUser={this.handleSelectUser} onSelectTopic={this.handleSelectTopic} />
                                : participationTile == DashboardUserAndGroupsParticipationSummaryColumn.KnowledgeTests
                                    ? <ParticipationKnowledgeTestGraph {...this.props} filter={filter} onSelectUser={this.handleSelectUser} onSelectTopic={this.handleSelectTopic} />
                                    : <ParticipationPublicationGraph {...this.props} tile={participationTile} filter={filter} onSelectUser={this.handleSelectUser} onSelectTopic={this.handleSelectTopic} />
                        }
                    </MuiPaper>
                </MuiContainer>
            </>
        );
    }
}

export default withSnackbar(withTranslation()(UserAndGroupsParticipationDashboard));